import type { StripeSchoolSeatsResponse, StripeSchoolTeachersResponse } from '@kimuno/api-sdk';
import immutable from 'object-path-immutable';

import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
	GET_STRIPE_SCHOOL_SEATS,
	GET_STRIPE_SCHOOL_TEACHERS,
	type StripeSchoolTeachersActionTypes,
} from '../actions/stripeSchoolTeachers.actionTypes';
import type { StoreItem } from '.';

export interface StripeSchoolTeachersState {
	teachers: StoreItem<StripeSchoolTeachersResponse>;
	/**
	 * `schoolId` names the school `data` belongs to — a refetch keeps the
	 * previous `data`. `requestId` tags the latest request: a response to any
	 * older one arrived out of order and is dropped.
	 */
	seats: StoreItem<StripeSchoolSeatsResponse> & { schoolId?: string; requestId?: number };
}

const initialState: StripeSchoolTeachersState = {
	teachers: { status: AsyncActionStatus.INITIAL },
	seats: { status: AsyncActionStatus.INITIAL },
};

// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature)
const stripeSchoolTeachers = (state = initialState, action: StripeSchoolTeachersActionTypes) => {
	switch (action.type) {
		case GET_STRIPE_SCHOOL_TEACHERS.START:
			return immutable(state).set(['teachers', 'status'], AsyncActionStatus.LOADING).value();

		case GET_STRIPE_SCHOOL_TEACHERS.SUCCESS:
			return immutable(state)
				.set(['teachers', 'data'], action.result)
				.set(['teachers', 'status'], AsyncActionStatus.READY)
				.set(['teachers', 'error'], undefined)
				.value();

		case GET_STRIPE_SCHOOL_TEACHERS.ERROR:
			return immutable(state)
				.set(['teachers', 'error'], action.error.message)
				.set(['teachers', 'status'], AsyncActionStatus.ERROR)
				.value();

		case GET_STRIPE_SCHOOL_SEATS.START:
			return immutable(state)
				.set(['seats', 'status'], AsyncActionStatus.LOADING)
				.set(['seats', 'requestId'], action.payload.requestId)
				.value();

		case GET_STRIPE_SCHOOL_SEATS.SUCCESS:
			if (action.payload.requestId !== state.seats.requestId) return state;
			return immutable(state)
				.set(['seats', 'data'], action.result)
				.set(['seats', 'schoolId'], action.payload.schoolId)
				.set(['seats', 'status'], AsyncActionStatus.READY)
				.set(['seats', 'error'], undefined)
				.value();

		case GET_STRIPE_SCHOOL_SEATS.ERROR:
			if (action.payload.requestId !== state.seats.requestId) return state;
			return immutable(state)
				.set(['seats', 'error'], action.error.message)
				.set(['seats', 'status'], AsyncActionStatus.ERROR)
				.value();

		default:
			return state;
	}
};

export default stripeSchoolTeachers;
